import React, { useState, useRef } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import type { ECharts } from 'echarts'
import type { PlatinumGraphScreenshotProps, CaptureOptions } from './types'
import { generateGraphOptions } from './utils/generateGraphOptions'

/**
 * PlatinumGraphScreenshot Component
 *
 * A utility component that can:
 * 1. Capture a chart as a Base64 image (capture mode)
 * 2. Display a pre-rendered Base64 image (preview mode)
 *
 * Usage for capture:
 * <PlatinumGraphScreenshot
 *   data={data}
 *   config={config}
 *   mode="capture"
 *   onScreenshotReady={(base64) => console.log(base64)}
 * />
 *
 * Usage for preview:
 * <PlatinumGraphScreenshot
 *   mode="preview"
 *   previewImage="data:image/png;base64,..."
 * />
 */
export const PlatinumGraphScreenshot: React.FC<PlatinumGraphScreenshotProps> = ({
  data,
  config,
  mode,
  onScreenshotReady,
  previewImage,
  displayAfterCapture = false,
  captureOptions = {},
  onError,
  className = '',
  isLoading = false,
}) => {
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [captureError, setCaptureError] = useState<string | null>(null)
  const echartsRef = useRef<ReactECharts | null>(null)
  const captureAttempted = useRef(false)

  // Default capture options
  const defaultCaptureOptions: Required<CaptureOptions> = {
    type: captureOptions.type || 'png',
    pixelRatio: captureOptions.pixelRatio || 2,
    backgroundColor: captureOptions.backgroundColor || '#fff',
    excludeComponents: captureOptions.excludeComponents || [],
  }

  /**
   * Handle chart ready event - this is when we can safely capture the screenshot
   */
  const handleChartReady = (chartInstance: ECharts) => {
    // Prevent multiple capture attempts
    if (captureAttempted.current) {
      return
    }
    captureAttempted.current = true

    // Small delay to ensure chart is fully painted
    setTimeout(() => {
      try {
        setIsCapturing(true)

        // Capture the chart as Base64 data URL
        const dataUrl = chartInstance.getDataURL({
          type: defaultCaptureOptions.type,
          pixelRatio: defaultCaptureOptions.pixelRatio,
          backgroundColor: defaultCaptureOptions.backgroundColor,
          excludeComponents: defaultCaptureOptions.excludeComponents,
        })

        // Call the callback with the Base64 string
        if (onScreenshotReady) {
          onScreenshotReady(dataUrl)
        }

        // Optionally display the captured image
        if (displayAfterCapture) {
          setCapturedImage(dataUrl)
        }

        setIsCapturing(false)
      } catch (error: any) {
        const errorMessage = `Failed to capture screenshot: ${error.message}`
        console.error(errorMessage, error)
        setCaptureError(errorMessage)
        setIsCapturing(false)

        if (onError) {
          onError(errorMessage)
        }
      }
    }, 1000)
  }

  // Preview mode: just display the image
  if (mode === 'preview') {
    if (!previewImage) {
      return (
        <Box
          className={className}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Typography color="text.secondary">No preview image provided</Typography>
        </Box>
      )
    }

    return (
      <Box className={className} sx={{ width: '100%', height: '100%' }}>
        <img
          src={previewImage}
          alt="Chart preview"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>
    )
  }

  // Capture mode: generate chart options and render
  if (mode === 'capture') {
    // If we've captured and should display, show the image
    if (displayAfterCapture && capturedImage) {
      return (
        <Box className={className} sx={{ width: '100%', height: '100%' }}>
          <img
            src={capturedImage}
            alt="Captured chart"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>
      )
    }

    // Parse and generate chart options using the utility function
    const result = generateGraphOptions(data, config)

    let chartOptions = null
    let parseError: string | null = null

    if (result.success) {
      chartOptions = result.chartOptions
    } else {
      parseError = result.error
      if (onError) {
        onError(result.error)
      }
    }

    // Handle errors
    if (parseError || captureError) {
      return (
        <Box
          className={className}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Typography color="error">{parseError || captureError}</Typography>
        </Box>
      )
    }

    // Handle loading
    if (isLoading || isCapturing) {
      return (
        <Box
          className={className}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 2,
          }}
        >
          <CircularProgress size={24} />
          <Typography color="text.secondary">
            {isLoading ? 'Loading data...' : 'Capturing screenshot...'}
          </Typography>
        </Box>
      )
    }

    // Render the chart off-screen for capture
    if (chartOptions) {
      return (
        <Box
          className={className}
          sx={{
            // Render off-screen but keep in layout for proper sizing
            position: 'absolute',
            left: '-9999px',
            top: '-9999px',
            width: '800px', // Fixed size for consistent screenshots
            height: '600px',
          }}
        >
          <ReactECharts
            ref={echartsRef}
            option={chartOptions}
            style={{
              width: '100%',
              height: '100%',
            }}
            onChartReady={handleChartReady}
            notMerge={true}
            lazyUpdate={false}
          />
        </Box>
      )
    }

    // No chart options generated
    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Typography color="text.secondary">Unable to generate chart</Typography>
      </Box>
    )
  }

  // Invalid mode
  return null
}

export default PlatinumGraphScreenshot
